import clsx from 'clsx';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import Badge from '@/src/components/common/Elements/Badge/Badge';
import LongTextByWidth from '@/src/components/common/Elements/LongText/LongTextByWidth';
import { RootState } from '@/src/reducers/types';

import Attachments from './Attachments/Attachments';
import EmailSenders from './EmailSenders/EmailSenders';

import { useInboxEntryPanel } from '../../../../store/inboxEntryPanel.store';
import {
  InboxEntry as InboxEntryType,
  InboxEntryFile,
  InboxEntryStatus,
  EmailThreadEntryViewerViews,
  SidebarEmailThreadEntry,
} from '../../../../types';
import useInboxSearchParams from '../../../../useInboxSearchParams';
import InboxEntry, { mapSidebarInboxEntryCommonProps } from '../InboxEntry';
import StatusIcon from '../StatusIcons/StatusIcon/StatusIcon';

const DEFAULT_SUBJECT = '<No Subject>';

export const mapEmailThreadEntry = (rawInboxEntry): SidebarEmailThreadEntry => {
  return {
    text: rawInboxEntry.thread.text,
    files: {
      list: (rawInboxEntry.thread.attachments || []).map((att) => ({
        ...att,
        isFromMessaging: Boolean(att?.conversation),
      })),
      count: rawInboxEntry.thread.attachments.length,
    },
    openInboundEmailsCount: rawInboxEntry.thread['open-inbound-emails-count'],
    openInboundAttachmentsCount:
      rawInboxEntry.thread['open-inbound-attachments-count'],
    emailEntriesCount: rawInboxEntry.thread['total-emails-count'],
    isAllEntriesOutbound: rawInboxEntry.thread['all-outbound'],
    status: rawInboxEntry.status,
    ...mapSidebarInboxEntryCommonProps(rawInboxEntry),
  };
};

export const getStatusIcons = (emailThreadEntry: SidebarEmailThreadEntry) => {
  const statusIcons = [];

  if (emailThreadEntry.isAllEntriesOutbound)
    return (
      <StatusIcon
        key={`icon_email_${emailThreadEntry.id}_outbound`}
        icon={'outbound'}
        containerClassName="tw-p-[5px] tw-mb-[3px]"
        iconClassName="tw-text-cfo-grey-600"
        svgProps={{ width: '20', height: '20', className: '' }}
      />
    );

  if (
    emailThreadEntry.status === InboxEntryStatus.Closed ||
    emailThreadEntry.openInboundEmailsCount === 0
  ) {
    statusIcons.push(
      <StatusIcon
        key={`icon_email_${emailThreadEntry.id}_closed`}
        icon="email"
        containerClassName="tw-p-[5px] tw-mb-[3px]"
        iconType="rounded"
        iconClassName="tw-text-cfo-blue-400 tw-text-[18px]"
      />
    );
  } else {
    statusIcons.push(
      <StatusIcon
        key={`icon_email_${emailThreadEntry.id}_openInboundEmailsCount`}
        icon="email_open"
        svgProps={{ className: 'tw-fill-cfo-green-300' }}
        containerClassName="tw-px-[17px] tw-py-[5px] tw-mb-[3px]"
        iconType={'rounded'}
        iconClassName="tw-mr-[5px]"
      >
        <span className={clsx('tw-text-cfo-black tw-font-brandonTextMedium')}>
          {emailThreadEntry.openInboundEmailsCount}
        </span>
      </StatusIcon>
    );
  }

  if (
    emailThreadEntry.files.list.length > 0 &&
    emailThreadEntry.openInboundAttachmentsCount === 0
  ) {
    statusIcons.push(
      <StatusIcon
        key={`icon_email_${emailThreadEntry.id}_closedFiles`}
        icon="close_file"
        svgProps={{ className: 'tw-fill-cfo-blue-400' }}
        containerClassName="tw-p-[5px] tw-mb-[3px]"
      />
    );
  } else {
    if (emailThreadEntry.openInboundAttachmentsCount) {
      statusIcons.push(
        <StatusIcon
          key={`icon_email_${emailThreadEntry.id}_activeFiles`}
          icon="active_file"
          svgProps={{ className: 'tw-fill-cfo-green-300' }}
          containerClassName="tw-px-[17px] tw-py-[5px] tw-mb-[3px]"
          iconType={'rounded'}
          iconClassName="tw-mr-[5px]"
        >
          <span className={clsx('tw-text-cfo-black tw-font-brandonTextMedium')}>
            {emailThreadEntry.openInboundAttachmentsCount}
          </span>
        </StatusIcon>
      );
    }
  }

  return (
    <React.Fragment>
      {statusIcons.map((statusIcon) => statusIcon)}
    </React.Fragment>
  );
};

const MAX_EMAIL_ENTRIES_COUNT_DISPLAY = 99;

export const getEmailEntriesCountDisplay = (emailEntriesCount: number) => {
  if (!emailEntriesCount) {
    return null;
  }

  return emailEntriesCount > MAX_EMAIL_ENTRIES_COUNT_DISPLAY
    ? `${MAX_EMAIL_ENTRIES_COUNT_DISPLAY}+`
    : emailEntriesCount;
};

export const getEmailContentPreview = (rawEmailContent: string) => {
  if (!rawEmailContent) {
    return '';
  }

  // Remove leading and trailing double quotes
  const sanitzedString = rawEmailContent.replace(/^"(.*)"$/, '$1');

  return sanitzedString.replace(/\\n/g, '');
};

type EmailThreadEntryProps = {
  inboxEntry: InboxEntryType;
};

const EmailThreadEntry = ({ inboxEntry }: EmailThreadEntryProps) => {
  const emailThreadEntry = inboxEntry.data as SidebarEmailThreadEntry;
  const isSidePanelCollapsed = useSelector(
    (store: RootState) => store.common.isSidePanelCollapsed
  );
  const { isTrash } = useInboxSearchParams();
  const showNewBadge = !isTrash && !emailThreadEntry.isAccountRead;
  const header = <EmailSenders senders={emailThreadEntry.from} />;
  // Consider memoizing var below once integration is complete, check render perf on isSidePanelCollapsed changes
  const statusIcons = getStatusIcons(emailThreadEntry);

  const subjectWidth = isSidePanelCollapsed ? 400 : 285;
  const emailPreviewWidth = isSidePanelCollapsed ? 400 : 300;
  const emailEntriesCountLabel = getEmailEntriesCountDisplay(
    emailThreadEntry.emailEntriesCount
  );
  const emailContentPreview = getEmailContentPreview(emailThreadEntry.text);
  const { setEmailThreadInboxEntry } = useInboxEntryPanel();

  const handleClickMore = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setEmailThreadInboxEntry(inboxEntry, {
        defaultView: EmailThreadEntryViewerViews.Attachments,
        preselectedFile: null,
      });
    },
    [inboxEntry, setEmailThreadInboxEntry]
  );

  const handleClickFile = useCallback(
    (file: InboxEntryFile) => {
      setEmailThreadInboxEntry(inboxEntry, {
        defaultView: EmailThreadEntryViewerViews.Attachments,
        preselectedFile: file,
      });
    },
    [inboxEntry, setEmailThreadInboxEntry]
  );

  const handleClickCard = useCallback(() => {
    setEmailThreadInboxEntry(inboxEntry, {
      defaultView: EmailThreadEntryViewerViews.EmailEntries,
      preselectedFile: null,
    });
  }, [inboxEntry, setEmailThreadInboxEntry]);

  return (
    <React.Fragment>
      <InboxEntry
        containerClassName={clsx('tw-w-full')}
        inboxEntry={inboxEntry}
        date={emailThreadEntry.createdDate}
        header={header}
        statusIcons={statusIcons}
        onClick={handleClickCard}
      >
        {/* Subject */}
        <div className={clsx('tw-flex tw-items-center')}>
          <LongTextByWidth
            key={`ee_subject_${emailThreadEntry.id}-${subjectWidth}`}
            id={`ee_subject_${emailThreadEntry.id}`}
            className={clsx(
              'tw-leading-[21px]',
              'tw-text-cfo-black',
              'tw-m-0',
              'tw-font-brandonTextMedium'
            )}
            value={emailThreadEntry.subject?.trim() || DEFAULT_SUBJECT}
            maxWidth={subjectWidth}
            highlightBySearchTerm={true}
          >
            <span
              className={clsx(
                'tw-leading-[21px]',
                'tw-text-[12px] tw-text-cfo-grey-600',
                'tw-mx-[5px]'
              )}
              data-testid="email_entries-count"
            >
              {emailEntriesCountLabel}
            </span>
            {showNewBadge && (
              <Badge
                label="New"
                className={clsx(
                  'tw-flex tw-items-center',
                  'tw-rounded-[34px] tw-leading-[18px]',
                  'tw-px-[10px] tw-py-0 tw-m-0',
                  'tw-text-white tw-text-[10px] tw-font-brandonTextMedium',
                  'tw-bg-cfo-blue-600'
                )}
              />
            )}
          </LongTextByWidth>
        </div>
        {/* Email Preview */}
        <LongTextByWidth
          key={`ee_raw_${emailThreadEntry.id}-${emailPreviewWidth}`}
          id={`ee_raw_${emailThreadEntry.id}`}
          className={clsx('tw-leading-[21px]', 'tw-text-cfo-black', 'tw-m-0')}
          value={emailContentPreview}
          maxWidth={emailPreviewWidth}
          hasTooltip={false}
          highlightBySearchTerm={true}
        />
        {emailThreadEntry.files.list.length > 0 && (
          <Attachments
            files={emailThreadEntry.files}
            onClickMore={handleClickMore}
            onClick={handleClickFile}
          />
        )}
      </InboxEntry>
    </React.Fragment>
  );
};

export default EmailThreadEntry;
